import { GetPopUpResponse, PopUpTrigger } from '../types';
import { getFromLocalStorage, isDefined, setLocalStorage } from './utils';

import topicData from './state/topic-data';
import { trackPopupEvent } from './api/popups-api';

/**
 * Select the best standalone popup to display based on priority, geo-trigger, cooldown, and time period
 */
export function selectStandalonePopup(trigger: PopUpTrigger): GetPopUpResponse | undefined {
    const now = new Date();

    // Filter by type, active status, and time period
    const eligible = topicData.standalonePopups.filter(popup => {
        if (!isDefined(popup._id) || !isDefined(popup.pop_up_type)) return false;
        if (popup.pop_up_type !== trigger || !popup.is_active) return false;

        // Check recurrence - enforce frequency limits
        const popupId = String(popup._id.$oid ?? '');
        const lastShownKey = `standalone_popup_shown_${popupId}`;
        const lastShownForRecurrence = getFromLocalStorage(lastShownKey);

        if (popup.recurrence === 'once') {
            // Once: never show again if already shown
            if (lastShownForRecurrence) return false;
        } else if (popup.recurrence === 'daily') {
            // Daily: only show once per calendar day
            if (lastShownForRecurrence) {
                const lastShown = new Date(lastShownForRecurrence);
                const today = new Date();
                if (lastShown.toDateString() === today.toDateString()) {
                    return false;
                }
            }
        } else if (popup.recurrence === 'weekly') {
            // Weekly: only show once per week (7 days)
            if (lastShownForRecurrence) {
                const lastShown = new Date(lastShownForRecurrence);
                const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                if (lastShown > oneWeekAgo) {
                    return false;
                }
            }
        } else if (popup.recurrence === 'monthly') {
            // Monthly: only show once per month (30 days)
            if (lastShownForRecurrence) {
                const lastShown = new Date(lastShownForRecurrence);
                const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                if (lastShown > oneMonthAgo) {
                    return false;
                }
            }
        }

        // Check time period
        if (isDefined(popup.active_from) && new Date(popup.active_from) > now) return false;
        if (isDefined(popup.active_until) && new Date(popup.active_until) < now) return false;

        // Check geo-trigger
        if (isDefined(popup.geo_trigger)) {
            const hasGeoPermission = 'geolocation' in navigator;
            if (!hasGeoPermission) return false;
            // Geo-check will be done asynchronously in displayStandalonePopup
        }

        return true;
    });

    // Sort by priority (highest first)
    eligible.sort((a, b) => b.priority - a.priority);

    return eligible[0];
}

/**
 * Check if user's current location is within the geo-trigger radius
 */
export async function checkGeoTrigger(geoTrigger: any, cachedLat?: number, cachedLng?: number): Promise<boolean> {
    // Use pre-fetched coordinates if available (from early geolocation request)
    if (cachedLat !== undefined && cachedLng !== undefined) {
        const distance = calculateDistance(cachedLat, cachedLng, geoTrigger.lat, geoTrigger.lng);
        return distance <= geoTrigger.radius_meters;
    }

    // Fall back to requesting location directly
    return await new Promise((resolve) => {
        if (!('geolocation' in navigator)) {
            resolve(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                const userLat = position.coords.latitude;
                const userLng = position.coords.longitude;
                const distance = calculateDistance(userLat, userLng, geoTrigger.lat, geoTrigger.lng);
                resolve(distance <= geoTrigger.radius_meters);
            },
            (error) => {
                console.error('Geolocation error:', error.code, error.message);
                resolve(false);
            },
            { enableHighAccuracy: false, timeout: 5000 }
        );
    });
}

/**
 * Calculate distance between two points using Haversine formula (in meters)
 */
export function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = (lat2 - lat1) * (Math.PI / 180);
    const dLng = (lng2 - lng1) * (Math.PI / 180);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * (Math.PI / 180)) *
        Math.cos(lat2 * (Math.PI / 180)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Mark a popup as shown for recurrence tracking
 */
export function markPopupAsShown(popup: GetPopUpResponse): void {
    if (isDefined(popup.recurrence)) {
        setLocalStorage(`standalone_popup_shown_${String(popup._id.$oid ?? '')}`, new Date().toISOString());
    }
}

/**
 * Track popup impression event
 */
export async function trackPopupImpression(popup: GetPopUpResponse): Promise<void> {
    if (topicData.topic?._id?.$oid) {
        await trackPopupEvent(popup._id.$oid, topicData.topic._id.$oid, 'impression');
    }
}
